import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

@Component({
  selector: 'app-view-details',
  templateUrl: './view-details.component.html',
  styleUrls: ['./view-details.component.scss']
})
export class ViewDetailsComponent implements OnInit {
  tab: Record<string, unknown> = {};
  entityData: Record<string, unknown> = {};
  schema: Record<string, unknown> = {};
  trafficData: Record<string, unknown> = {};
  signalData: Record<string, unknown> = {};

  basicOptions: Record<string, unknown> = {};
  signalOptions: Record<string, unknown> = {};
  trafficOptions: Record<string, unknown> = {};

  deviceFieldsToDisplay: { key: string; value: unknown }[] = [];

  constructor(
    public config: DynamicDialogConfig,
    private ref: DynamicDialogRef
  ) {}

  ngOnInit(): void {
    this.entityData = this.config.data.entityData;
    this.tab = this.config.data.tab;
    this.schema = this.config.data.schema;

    this.deviceFieldsToDisplay = Object.entries(this.entityData).map(([key, value]) => {
      return { key: key, value: value };
    });

    this.basicOptions = {
      plugins: {
        legend: {
          display: true
        },
        title: {
          display: true,
          text: 'My Title',
          fontSize: 16
        }
      }
    };

    this.trafficData = this.generateFakeTrafficData(new Date(), 30);

    this.trafficOptions = {
      ...this.basicOptions,
      plugins: {
        title: { display: true, text: 'Traffic Data', fontSize: 16 }
      }
    };

    this.signalData = this.generateFakeSignalData(new Date(), 30);

    this.signalOptions = {
      ...this.basicOptions,
      plugins: {
        title: { display: true, text: 'Signal Data', fontSize: 16 }
      }
    };
  }

  closeDialog(): void {
    this.ref.close();
  }

  /**
   * Get tab name safely
   */
  getTabName(): string {
    return (this.tab['name'] as string) || 'Entity';
  }

  /**
   * Type guard methods for safe template usage
   */
  isStringValue(value: unknown): value is string {
    return typeof value === 'string' && value !== null && value !== undefined;
  }

  isNumberValue(value: unknown): value is number {
    return typeof value === 'number' && !isNaN(value);
  }

  isBooleanValue(value: unknown): value is boolean {
    return typeof value === 'boolean';
  }

  isDateValue(value: unknown): value is Date | string | number {
    if (value instanceof Date) return true;
    if (typeof value === 'string' && !isNaN(Date.parse(value))) return true;
    if (typeof value === 'number' && !isNaN(value)) return true;
    return false;
  }

  /**
   * Format field label from snake_case to Title Case
   */
  formatFieldLabel(key: string): string {
    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Determine the field type based on key and value
   */
  getFieldType(key: string, value: unknown): string {
    if (!value && value !== false) return 'text';

    // Check for specific field patterns
    if (
      key.toLowerCase().includes('date') ||
      key.toLowerCase().includes('created') ||
      key.toLowerCase().includes('updated')
    ) {
      return 'date';
    }

    if (key.toLowerCase().includes('email')) {
      return 'email';
    }

    if (key.toLowerCase().includes('url') || key.toLowerCase().includes('link')) {
      return 'url';
    }

    if (key.toLowerCase().includes('status')) {
      return 'status';
    }

    if (
      key.toLowerCase().includes('amount') ||
      key.toLowerCase().includes('price') ||
      key.toLowerCase().includes('cost')
    ) {
      return 'currency';
    }

    if (
      key.toLowerCase().includes('count') ||
      key.toLowerCase().includes('number') ||
      key.toLowerCase().includes('id')
    ) {
      return 'number';
    }

    if (
      key.toLowerCase().includes('active') ||
      key.toLowerCase().includes('enabled') ||
      key.toLowerCase().includes('is_')
    ) {
      return 'boolean';
    }

    // Check value type
    if (typeof value === 'boolean') {
      return 'boolean';
    }

    if (typeof value === 'number') {
      return 'number';
    }

    if (value instanceof Date) {
      return 'date';
    }

    if (typeof value === 'string') {
      // Check if it's a URL
      if (value.startsWith('http://') || value.startsWith('https://')) {
        return 'url';
      }

      // Check if it's an email
      if (value.includes('@') && value.includes('.')) {
        return 'email';
      }

      // Check if it's a date string
      if (!isNaN(Date.parse(value))) {
        return 'date';
      }
    }

    return 'text';
  }

  /**
   * Get severity for status tags
   */
  getStatusSeverity(status: unknown): string {
    if (!this.isStringValue(status)) return 'info';

    const statusLower = status.toLowerCase();

    switch (statusLower) {
      case 'active':
      case 'completed':
      case 'success':
      case 'approved':
        return 'success';
      case 'pending':
      case 'warning':
        return 'warning';
      case 'inactive':
      case 'failed':
      case 'error':
      case 'cancelled':
        return 'danger';
      case 'draft':
      case 'info':
      case 'processing':
        return 'info';
      default:
        return 'info';
    }
  }

  getFieldValue(field: string): unknown {
    const fieldParts = field.split('.');
    let value: unknown = this.entityData;
    for (const part of fieldParts) {
      if (value && typeof value === 'object' && value !== null) {
        value = (value as Record<string, unknown>)[part];
      } else {
        return null;
      }
    }
    return value;
  }

  generateFakeTrafficData(startDate: Date, days: number = 30) {
    const labels: string[] = [];
    const downloadData: string[] = [];
    const uploadData: string[] = [];
    const currentDate = new Date(startDate);

    for (let i = 0; i < days; i++) {
      // Format the date as YYYY-MM-DD
      const dateString = currentDate.toISOString().split('T')[0];
      labels.push(dateString);

      // Generate random data for download and upload in Mbps
      const downloadSpeed = Math.random() * (60 - 40) + 40; // Download speed between 20 and 100 Mbps
      const uploadSpeed = Math.random() * (30 - 20) + 20; // Upload speed between 5 and 50 Mbps

      downloadData.push(downloadSpeed.toFixed(2));
      uploadData.push(uploadSpeed.toFixed(2));

      // Move to the next day
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return {
      labels: labels,
      datasets: [
        {
          label: 'Download (daily average)',
          data: downloadData,
          fill: false,
          borderColor: '#42A5F5',
          tension: 0.1
        },
        {
          label: 'Upload (daily average)',
          data: uploadData,
          fill: false,
          borderColor: '#FFA726',
          tension: 0.1
        }
      ]
    };
  }

  generateFakeSignalData(startDate: Date, days: number = 30) {
    const labels: string[] = [];
    const signalData: string[] = [];
    const currentDate = new Date(startDate);

    for (let i = 0; i < days; i++) {
      // Format the date as YYYY-MM-DD
      const dateString = currentDate.toISOString().split('T')[0];
      labels.push(dateString);

      // Generate random signal data in dBm between -25 and -20
      const signalStrength = Math.random() * (-20 - -25) + -25;

      signalData.push(signalStrength.toFixed(2));

      // Move to the next day
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return {
      labels: labels,
      datasets: [
        {
          label: 'Signal Strength (daily average)',
          data: signalData,
          fill: false,
          borderColor: '#42A5F5',
          tension: 0.1
        }
      ]
    };
  }
}
